import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatRelativeTime } from '../../qCockpit/components/SharedFrames';
import { normalizeClientAlias } from '../../strategyDesk/clientAlias';
import { shellSx, shellTokens } from '../../shell/theme/shellTheme';

function formatHeartbeatAge(heartbeatAgeSec, lastHeartbeatAt) {
    if (heartbeatAgeSec != null) {
        if (heartbeatAgeSec < 60) {
            return `${heartbeatAgeSec}s ago`;
        }
        const minutes = Math.floor(heartbeatAgeSec / 60);
        return `${minutes}m ago`;
    }
    if (lastHeartbeatAt) {
        return formatRelativeTime(lastHeartbeatAt);
    }
    return 'No heartbeat';
}

function statusTone(row) {
    if (row.isHalted) {
        return 'error';
    }
    if (row.isStale) {
        return 'warning';
    }
    return 'success';
}

function toneStyles(tone) {
    if (tone === 'error') {
        return {
            borderColor: 'rgba(220, 38, 38, 0.3)',
            bgcolor: 'rgba(220, 38, 38, 0.05)',
            color: shellTokens.error,
        };
    }
    if (tone === 'warning') {
        return {
            borderColor: 'rgba(217, 119, 6, 0.3)',
            bgcolor: 'rgba(217, 119, 6, 0.06)',
            color: shellTokens.warning,
        };
    }
    return {
        borderColor: 'rgba(22, 163, 74, 0.25)',
        bgcolor: 'rgba(22, 163, 74, 0.05)',
        color: shellTokens.success,
    };
}

const SummaryChip = ({ label, value, tone = 'default' }) => {
    const toneSx =
        tone === 'warning'
            ? { color: shellTokens.warning }
            : tone === 'error'
                ? { color: shellTokens.error }
                : tone === 'success'
                    ? { color: shellTokens.success }
                    : { color: shellTokens.text };

    return (
        <Box
            sx={{
                px: 1.25,
                py: 0.85,
                borderRadius: `${shellTokens.innerRadius}px`,
                border: `1px solid ${shellTokens.border}`,
                minWidth: 88,
            }}
        >
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: shellTokens.muted, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, ...toneSx }}>{value}</Typography>
        </Box>
    );
};

const HeartbeatPanel = ({ heartbeats = [], summary, loading }) => {
    if (loading) {
        return (
            <Box sx={{ ...shellSx.panel, mb: 1.75 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                    <Skeleton width={160} height={20} />
                </Box>
                <Box sx={{ p: 2 }}>
                    <Skeleton variant="rounded" height={72} sx={{ mb: 1 }} />
                    <Skeleton variant="rounded" height={72} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ ...shellSx.panel, mb: 1.75 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                <Typography
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: shellTokens.muted,
                    }}
                >
                    Heartbeats
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 0.25 }}>
                    Stale / halt summary
                </Typography>
            </Box>

            <Box sx={{ p: '14px 16px' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <SummaryChip label="Instances" value={summary?.totalInstances ?? heartbeats.length} />
                    <SummaryChip label="Fresh" value={summary?.freshCount ?? 0} tone="success" />
                    <SummaryChip label="Stale" value={summary?.staleCount ?? 0} tone="warning" />
                    <SummaryChip label="Halted" value={summary?.haltedCount ?? 0} tone="error" />
                </Box>

                {!heartbeats.length ? (
                    <Typography sx={{ fontSize: '0.82rem', color: shellTokens.muted }}>
                        No strategy instances reporting telemetry.
                    </Typography>
                ) : (
                    <Box>
                        {heartbeats.map((row) => {
                            const tone = statusTone(row);
                            const styles = toneStyles(tone);
                            const client = normalizeClientAlias(row.clientAlias, row.clientId);
                            const label = row.strategyLabel || row.strategyId;

                            return (
                                <Box
                                    key={row.instanceId}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr auto' },
                                        gap: 1,
                                        alignItems: 'center',
                                        px: 1.5,
                                        py: 1.15,
                                        mb: 0.85,
                                        borderRadius: `${shellTokens.innerRadius}px`,
                                        border: `1px solid ${shellTokens.border}`,
                                        ...styles,
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: shellTokens.text }}>
                                            {label} · {client}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: shellTokens.muted, mt: 0.25 }}>
                                            {row.status || 'unknown'} · {row.mode || 'live'}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.78rem', color: shellTokens.muted }}>
                                        Last heartbeat: {formatHeartbeatAge(row.heartbeatAgeSec, row.lastHeartbeatAt)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            justifySelf: { sm: 'end' },
                                        }}
                                    >
                                        {row.isHalted ? 'Halted' : row.isStale ? 'Stale' : 'Live'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default HeartbeatPanel;
